import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import Modal from '../../components/Modal'
import Button from '../../components/Button'
import Confirmation from '../../components/Confirmation/Confirmation'
import { Input, Select, Textarea } from '../../components/ModalInputs'
import { createScheduleVariant, updateScheduleVariant } from '../../actions/doScheduleVariants'
import { getCurrentYearUtils } from '../../utils/classBook'
import styles from './styles.module.scss'

const validationSchema = Yup.object({
  sh_var_name: Yup.string().trim().required('Укажите название варианта расписания').max(30, 'Должно быть не более 30 символов'),
  sh_var_comments: Yup.string().max(256, 'Должно быть не более 256 символов'),
})

export default function ScheduleVariantsDOModal({ data, closeModal, schoolYears, yearId }) {
  const dispatch = useDispatch()
  const { loading } = useSelector(state => state.do_schedule_variants)
  const addMode = !data?.sh_var_id

  const [confirmClose, setConfirmClose] = useState(false)
  const acceptConfirmation = () => { closeModal(); setConfirmClose(false) }
  const rejectConfirmation = () => setConfirmClose(false)

  // при фильтре «Все» подставляем текущий учебный год
  const defaultYearId = yearId || getCurrentYearUtils(schoolYears) || schoolYears?.[0]?.xp_key || ''
  const emptyValues = { s_year_id: defaultYearId, sh_var_name: '', sh_var_comments: '' }

  // тело POST/PUT — строго { s_year_id, sh_var_name, sh_var_comments }
  const buildPayload = (values) => ({
    s_year_id: Number(values.s_year_id),
    sh_var_name: values.sh_var_name.trim(),
    sh_var_comments: values.sh_var_comments || ''
  })

  const handleCreate = async (values) => {
    const success = await dispatch(createScheduleVariant(buildPayload(values)))
    success && closeModal()
  }

  const handleUpdate = async (values) => {
    const success = await dispatch(updateScheduleVariant(values.sh_var_id, buildPayload(values)))
    success && closeModal()
  }

  const renderOptions = ({ name, xp_key }) => <option key={xp_key} value={xp_key}>{name}</option>

  const {
    values,
    errors,
    touched,
    submitCount,
    dirty,
    handleBlur,
    handleChange,
    handleSubmit,
    resetForm
  } = useFormik({
    initialValues: addMode ? emptyValues : {
      sh_var_id: data.sh_var_id,
      s_year_id: data.s_year_id || defaultYearId,
      sh_var_name: data.sh_var_name || '',
      sh_var_comments: data.sh_var_comments || ''
    },
    enableReinitialize: true,
    validateOnChange: true,
    validationSchema,
    onSubmit: addMode ? handleCreate : handleUpdate
  })

  // закрытие: если есть несохранённые изменения — спрашиваем подтверждение
  const handleClose = () => dirty ? setConfirmClose(true) : closeModal()

  useEffect(() => {
    if (data && addMode) {
      resetForm({ values: emptyValues })
    }
  }, [data])

  const error = (fieldName) => (touched[fieldName] || submitCount) && errors[fieldName]

  return (
    <Modal open={!!data} onClose={handleClose} closeOnOverlayClick={true} closeOnEsc={true} compact classNames={{ modal: styles.modal }}>
      <div className={styles.modalMain}>
        <h3 className={styles.modalTitle}>{addMode ? 'Новый вариант' : 'Изменение варианта'}</h3>
        <div className={styles.inputsRow}>
          <Input
            label='Название варианта'
            name='sh_var_name'
            value={values.sh_var_name}
            error={error('sh_var_name')}
            onChange={handleChange}
            onBlur={handleBlur}
          />
        </div>
        <div className={styles.inputsRow}>
          <Select
            label='Учебный год'
            name='s_year_id'
            value={values.s_year_id}
            onChange={handleChange}
            onBlur={handleBlur}
          >
            {schoolYears?.map(renderOptions)}
          </Select>
        </div>
        <div className={styles.inputsRow}>
          <Textarea
            label='Описание варианта'
            name='sh_var_comments'
            value={values.sh_var_comments}
            onChange={handleChange}
            error={error('sh_var_comments')}
          />
        </div>
      </div>
      <div className={styles.modalFooter}>
        <Button mode='white' onClick={handleSubmit} loading={loading} disabled={loading}>Сохранить</Button>
        <Button mode='white' onClick={handleClose}>Закрыть</Button>
      </div>
      {confirmClose && (
        <Confirmation
          title="Закрыть без сохранения изменений?"
          acceptConfirmation={acceptConfirmation}
          rejectConfirmation={rejectConfirmation}
        />
      )}
    </Modal>
  )
}
